import React, { Fragment, useEffect, useState } from 'react';
import { useStyles, cx } from '../../../lib/styles';
import sliderStyles from './Slider_styles';
import Carousel from 'nuka-carousel';
import Icon from '../Icon';
import range from 'lodash/range';
import { Brand } from '../../../lib/theme';

/** An image inside the slider. */
export interface SliderImage {
  /** The path (URL) to the image. */
  path: string;

  /** The description/caption of the image. */
  description?: string;
}

export interface SliderProps {
  /** The minimum width to display the slider at. */
  minWidth: number;

  /** The maximum width to display the slider at. */
  maxWidth: number;

  /**
   * The aspect ratio of the image (height divided by width).
   *
   * @example 9/16
   */
  aspectRatio: number;

  /** The event listener for when a specific slide is clicked into. */
  onGotoSlide?: (i: number) => any;

  /** The event listener for when the next slide is clicked. */
  onNextSlide?: () => any;

  /** The event listener for when the previous slide is clicked. */
  onPreviousSlide?: () => any;

  /** The images to display in the slider. */
  images: SliderImage[];

  /* Any additional class names for the container element. */
  className?: string;

  /** The brand to use for colouring the slider. */
  brand?: Brand;
}

/** A sliding gallery of images. */
export const Slider = (props: SliderProps) => {
  const {
    images,
    className,
    onGotoSlide,
    onNextSlide,
    onPreviousSlide,
    brand = 'sas',
  } = props;
  const [description, setDescription] = useState(
    images[0] ? images[0].description : ''
  );
  const [isClientMounted, setIsClientMounted] = useState(false);
  const styles = useStyles(sliderStyles(props));

  useEffect(() => {
    setIsClientMounted(true);
  }, []);

  const renderCenterLeftControls = ({ currentSlide, previousSlide }) =>
    currentSlide ? (
      <button
        type={'button'}
        className={styles.btn}
        onClick={() => {
          previousSlide();
          if (onPreviousSlide) onPreviousSlide();
        }}
      >
        <Icon
          icon={'chevron_left'}
          size={'xxl'}
          brand={brand}
          container={false}
        />
      </button>
    ) : (
      <span />
    );

  const renderCenterRightControls = ({ currentSlide, slideCount, nextSlide }) =>
    currentSlide !== slideCount - 1 ? (
      <button
        type={'button'}
        className={styles.btn}
        onClick={() => {
          nextSlide();
          if (onNextSlide) onNextSlide();
        }}
      >
        <Icon
          icon={'chevron_right'}
          size={'xxl'}
          brand={brand}
          container={false}
        />
      </button>
    ) : (
      <span />
    );

  const renderBottomCenterControls = ({
    slideCount,
    goToSlide,
    currentSlide,
  }) => (
    <div className={styles.dots}>
      {slideCount > 1 &&
        range(slideCount).map((i: number) => (
          <button
            key={i}
            className={
              currentSlide === i ? cx(styles.activeDot, styles.dot) : styles.dot
            }
            onClick={() => {
              goToSlide(i);
              if (onGotoSlide) onGotoSlide(i);
            }}
          />
        ))}
    </div>
  );

  const afterSlide = slideIndex => {
    setDescription(
      (images[slideIndex] && images[slideIndex].description) || ''
    );
  };

  if (!isClientMounted) {
    const firstImage = images[0];

    return (
      <div>
        <div className={cx('Slider', styles.slider, className)}>
          {firstImage && (
            <>
              {brand == 'travel360' && (
                <img className={styles.slideBackdrop} src={firstImage.path} />
              )}
              <img className={styles.slide} src={firstImage.path} />
            </>
          )}
        </div>
        {description && <div className={styles.description}>{description}</div>}
      </div>
    );
  }

  return (
    <div>
      <Carousel
        className={cx('Slider', styles.slider, className)}
        renderCenterLeftControls={renderCenterLeftControls}
        renderCenterRightControls={renderCenterRightControls}
        renderBottomCenterControls={renderBottomCenterControls}
        afterSlide={afterSlide}
        style={{
          width: '100%',
        }}
      >
        {images.map((image, i) => {
          return (
            <Fragment key={i}>
              {brand == 'travel360' && (
                <img className={styles.slideBackdrop} src={image.path} />
              )}
              <img className={styles.slide} src={image.path} />
            </Fragment>
          );
        })}
      </Carousel>

      {description && <div className={styles.description}>{description}</div>}
    </div>
  );
};

export default Slider;
